<script lang="ts" setup>
import { useDialogPluginComponent } from 'quasar'
import { useI18n } from 'vue-i18n'
import { ref } from 'vue'

import SectionHeader from 'components/SectionHeader.vue'
import CookieCategoryExpandItem from 'components/CookieCategoryExpandItem.vue'
import useUserConsent from 'src/composables/useUserConsent'
import eventBus from 'boot/event-bus'

const WEB_URL = process.env.WEB_URL

defineEmits([...useDialogPluginComponent.emits])

const { isConsentGiven, cookiePreferences, acceptAll, acceptNecessary, saveConsent } =
  useUserConsent()

const { t } = useI18n()
const showDialog = ref(!isConsentGiven())
const showDetails = ref(false)

eventBus.on('open-cookie-preferences', () => {
  showDialog.value = true
})
</script>

<template>
  <q-dialog
    v-model="showDialog"
    :transition-duration="500"
    full-width
    persistent
    position="bottom"
    seamless
  >
    <q-card class="cookie-dialog" flat>
      <SectionHeader :title="t('dialog.cookie.title')" class="header">
        <template #right>
          <q-btn dense flat icon="fa-regular fa-xmark" round @click="showDialog = false" />
        </template>
      </SectionHeader>

      <div class="cookie-content q-py-md">
        <p>{{ t('dialog.cookie.description') }}</p>

        <transition :duration="200" name="show-details">
          <div v-if="showDetails" class="cookie-options q-mb-md">
            <CookieCategoryExpandItem
              v-model="cookiePreferences.necessary"
              :info="t('dialog.cookie.necessary.info')"
              :label="t('dialog.cookie.necessary.label')"
              disable
            />
            <CookieCategoryExpandItem
              v-model="cookiePreferences.diagnostics"
              :info="t('dialog.cookie.diagnostics.info')"
              :label="t('dialog.cookie.diagnostics.label')"
            />
            <CookieCategoryExpandItem
              v-model="cookiePreferences.analytics"
              :info="t('dialog.cookie.analytics.info')"
              :label="t('dialog.cookie.analytics.label')"
            />
            <CookieCategoryExpandItem
              v-model="cookiePreferences.marketing"
              :info="t('dialog.cookie.marketing.info')"
              :label="t('dialog.cookie.marketing.label')"
            />
          </div>
        </transition>

        <div class="cookie-actions">
          <q-btn
            class="full-width border-radius-2"
            color="white"
            no-caps
            text-color="dark"
            unelevated
            @click="acceptNecessary"
          >
            {{ t('dialog.cookie.acceptNecessary') }}
          </q-btn>
          <q-btn
            v-if="showDetails"
            class="full-width border-radius-2"
            color="white"
            no-caps
            text-color="dark"
            unelevated
            @click="saveConsent"
          >
            {{ t('dialog.cookie.savePreferences') }}
          </q-btn>
          <q-btn
            v-else
            class="full-width border-radius-2"
            color="white"
            no-caps
            text-color="dark"
            unelevated
            @click="showDetails = true"
          >
            {{ t('dialog.cookie.showDetails') }}
          </q-btn>
          <q-btn
            class="full-width border-radius-2"
            color="dark"
            no-caps
            unelevated
            @click="acceptAll"
          >
            {{ t('dialog.cookie.acceptAll') }}
          </q-btn>
        </div>

        <div class="text-caption text-center q-mt-md">
          {{ t('dialog.cookie.moreOn') }}
          <a
            :href="`${WEB_URL}/privacy-policy`"
            style="color: inherit; font-weight: normal"
            target="_blank"
          >
            {{ t('dialog.cookie.privacyPolicy') }}
          </a>
        </div>
      </div>
    </q-card>
  </q-dialog>
</template>

<style lang="scss" scoped>
@import 'src/css/quasar.variables';

.cookie-dialog {
  width: 100%;
  max-width: $max-web-width !important;
  border-radius: $border-radius-3 $border-radius-3 0 0;
  border: 1px solid $dark;
  border-bottom: none;
  background: $primary-400;

  .header {
    position: sticky;
    top: 0;
    z-index: 1;
    background: $primary;
    padding: 24px 24px 0;
  }

  .cookie-content {
    width: 100%;
    padding: 0 24px 24px;

    .cookie-actions {
      display: flex;
      flex-wrap: wrap;
      gap: $space-3;

      > *:last-child {
        order: -1;
      }

      @include sm() {
        flex-wrap: nowrap;

        > *:last-child {
          order: 0;
        }
      }
    }
  }

  .show-details {
    overflow: hidden;

    &-enter-active,
    &-leave-active {
      transition:
        opacity 500ms ease-in-out 300ms,
        height 300ms ease-in-out;
      height: 230px;
    }

    &-enter-from,
    &-leave-to {
      opacity: 0;
      height: 0;
    }
  }
}
</style>
